import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash, Play, X, Globe, Key, Lock, CheckCircle, Spinner, Eye, EyeSlash, ListMagnifyingGlass } from '@phosphor-icons/react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import APICatalog from '@/components/APICatalog';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];
const AUTH_TYPES = [
  { value: 'none', label: 'No Auth' },
  { value: 'bearer', label: 'Bearer Token' },
  { value: 'api_key', label: 'API Key' },
];

export default function Connections() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingConnection, setEditingConnection] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', base_url: '', method: 'GET', headers: {}, auth_type: 'none', auth_config: {} });
  const [templateCredentials, setTemplateCredentials] = useState({});
  const [testingTemplate, setTestingTemplate] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showSecrets, setShowSecrets] = useState({});
  const [headerKey, setHeaderKey] = useState('');
  const [headerValue, setHeaderValue] = useState('');
  const [catalogOpen, setCatalogOpen] = useState(false);

  useEffect(() => {
    fetchData();
    if (searchParams.get('action') === 'new') { setTemplateDialogOpen(true); setSearchParams({}); }
  }, []);

  const fetchData = async () => {
    try {
      const [connRes, tplRes] = await Promise.all([
        fetch(`${API}/connections`, { credentials: 'include' }),
        fetch(`${API}/templates`, { credentials: 'include' })
      ]);
      if (connRes.ok) setConnections(await connRes.json());
      if (tplRes.ok) setTemplates(await tplRes.json());
    } catch (error) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingConnection ? `${API}/connections/${editingConnection.connection_id}` : `${API}/connections`;
      const response = await fetch(url, { method: editingConnection ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(formData) });
      if (response.ok) { toast.success(editingConnection ? 'Connection updated' : 'Connection created'); setDialogOpen(false); resetForm(); fetchData(); }
      else { const error = await response.json(); toast.error(error.detail || 'Failed to save'); }
    } catch (error) { toast.error('Failed to save'); }
  };

  const handleDelete = async (connectionId) => {
    if (!window.confirm('Delete this connection?')) return;
    try {
      const response = await fetch(`${API}/connections/${connectionId}`, { method: 'DELETE', credentials: 'include' });
      if (response.ok) { toast.success('Connection deleted'); fetchData(); } else toast.error('Failed to delete');
    } catch (error) { toast.error('Failed to delete'); }
  };

  const handleEdit = (connection) => {
    setEditingConnection(connection);
    setFormData({ name: connection.name, description: connection.description || '', base_url: connection.base_url, method: connection.method, headers: connection.headers || {}, auth_type: connection.auth_type, auth_config: connection.auth_config || {} });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingConnection(null);
    setFormData({ name: '', description: '', base_url: '', method: 'GET', headers: {}, auth_type: 'none', auth_config: {} });
    setHeaderKey(''); setHeaderValue('');
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setTemplateCredentials({});
    setTestResult(null);
    setShowSecrets({});
  };

  const handleTestTemplate = async () => {
    if (!selectedTemplate) return;
    setTestingTemplate(true);
    setTestResult(null);
    try {
      const response = await fetch(`${API}/templates/${selectedTemplate.template_id}/test`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ credentials: templateCredentials })
      });
      const data = await response.json();
      setTestResult(data);
      if (data.success) toast.success(`Connected in ${data.response_time_ms}ms`);
      else toast.error(data.error || 'Connection failed');
    } catch (error) { toast.error('Test failed'); setTestResult({ success: false, error: 'Network error' }); }
    finally { setTestingTemplate(false); }
  };

  const handleCreateFromTemplate = async () => {
    if (!selectedTemplate || !testResult?.success) return;
    try {
      const response = await fetch(`${API}/templates/${selectedTemplate.template_id}/connect`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ credentials: templateCredentials, name: selectedTemplate.display_name })
      });
      if (response.ok) {
        toast.success(`${selectedTemplate.display_name} connected!`);
        setTemplateDialogOpen(false); setSelectedTemplate(null); fetchData();
      } else { const error = await response.json(); toast.error(error.detail || 'Failed to create'); }
    } catch (error) { toast.error('Failed to create connection'); }
  };

  const addHeader = () => { if (headerKey && headerValue) { setFormData(prev => ({ ...prev, headers: { ...prev.headers, [headerKey]: headerValue } })); setHeaderKey(''); setHeaderValue(''); } };
  const removeHeader = (key) => { const newHeaders = { ...formData.headers }; delete newHeaders[key]; setFormData(prev => ({ ...prev, headers: newHeaders })); };
  const getMethodColor = (method) => ({ GET: 'text-kova-green', POST: 'text-kova-cyan', PUT: 'text-kova-amber', DELETE: 'text-kova-red', PATCH: 'text-kova-purple' }[method] || 'text-white');

  const handleCatalogSelect = (template) => {
    setCatalogOpen(false);
    setFormData({
      name: template.name,
      description: template.description || '',
      base_url: template.base_url,
      method: template.method || 'GET',
      headers: template.headers || {},
      auth_type: template.auth_type || 'none',
      auth_config: template.auth_config || {},
    });
    setDialogOpen(true);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><span className="font-mono text-kova-cyan">LOADING CONNECTIONS...</span></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading font-bold text-2xl">API Connections</h1><p className="text-kova-slate text-sm mt-1">Manage your API integrations and configurations</p></div>
        <div className="flex gap-2">
          <Button data-testid="browse-catalog-button" onClick={() => setCatalogOpen(true)} variant="outline" className="border-kova-cyan/12 text-kova-silv hover:text-kova-white hover:border-kova-cyan/25 rounded-none bg-transparent"><ListMagnifyingGlass size={16} className="mr-2" />Browse Catalog</Button>
          <Button data-testid="add-connection-button" onClick={() => setTemplateDialogOpen(true)} className="bg-kova-cyan text-kova-bg hover:bg-kova-cyan/90 rounded-none"><Plus size={16} className="mr-2" />Add Connection</Button>
        </div>
      </div>

      {connections.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnimatePresence>
            {connections.map((conn, index) => (
              <motion.div key={conn.connection_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ delay: index * 0.05 }} className="card-brutal p-5 flex flex-col" data-testid={`connection-card-${conn.connection_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`font-mono text-xs font-bold ${getMethodColor(conn.method)}`}>{conn.method}</span>
                    {conn.auth_type !== 'none' && <Lock size={12} className="text-kova-amber" />}
                    {conn.provider && <span className="text-kova-slate text-xs font-mono uppercase">{conn.provider}</span>}
                  </div>
                  <div className={`w-2 h-2 rounded-full ${conn.is_active ? 'bg-kova-green' : 'bg-kova-slate'}`} />
                </div>
                <h3 className="font-heading font-bold text-lg mb-1 truncate">{conn.name}</h3>
                <p className="text-kova-slate text-sm mb-3 line-clamp-2">{conn.description || 'No description'}</p>
                <div className="flex items-center gap-2 mb-4 overflow-hidden"><Globe size={14} className="text-kova-slate flex-shrink-0" /><span className="font-mono text-xs text-kova-silv truncate">{conn.base_url}</span></div>
                <div className="flex items-center gap-2 mt-auto pt-3 border-t border-kova-cyan/12">
                  <button data-testid={`view-${conn.connection_id}`} onClick={() => navigate(`/connections/${conn.connection_id}`)} className="flex-1 flex items-center justify-center gap-2 py-2 bg-kova-card2 text-kova-silv text-sm font-medium hover:text-white hover:bg-kova-cyan/12 transition-colors"><Eye size={14} />View</button>
                  <button data-testid={`execute-${conn.connection_id}`} onClick={() => navigate(`/executor/${conn.connection_id}`)} className="flex-1 flex items-center justify-center gap-2 py-2 bg-kova-cyan/10 text-kova-cyan text-sm font-medium hover:bg-kova-cyan/20 transition-colors"><Play size={14} weight="fill" />Execute</button>
                  <button data-testid={`edit-${conn.connection_id}`} onClick={() => handleEdit(conn)} className="p-2 text-kova-silv hover:text-white hover:bg-kova-card2 transition-colors"><Pencil size={16} /></button>
                  <button data-testid={`delete-${conn.connection_id}`} onClick={() => handleDelete(conn.connection_id)} className="p-2 text-kova-silv hover:text-kova-red hover:bg-kova-red/10 transition-colors"><Trash size={16} /></button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      ) : (
        <div className="card-brutal p-12 text-center"><Globe size={48} className="text-kova-cyan/12 mx-auto mb-4" /><h3 className="font-heading font-bold text-xl mb-2">No connections yet</h3><p className="text-kova-slate mb-6">Add your first API connection to get started</p><Button onClick={() => setTemplateDialogOpen(true)} className="bg-kova-cyan text-kova-bg hover:bg-kova-cyan/90 rounded-none"><Plus size={16} className="mr-2" />Add Connection</Button></div>
      )}

      {/* Template Gallery Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={(open) => { setTemplateDialogOpen(open); if (!open) { setSelectedTemplate(null); setTestResult(null); } }}>
        <DialogContent className="bg-kova-card border-kova-cyan/12 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading font-bold text-xl">{selectedTemplate ? `Connect ${selectedTemplate.display_name}` : 'Add Connection'}</DialogTitle></DialogHeader>
          
          {!selectedTemplate ? (
            <div className="space-y-4 mt-4">
              <p className="text-kova-slate text-sm">Choose a template or connect from scratch</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {templates.map((tpl) => (
                  <button key={tpl.template_id} onClick={() => handleTemplateSelect(tpl)} className="p-4 border border-kova-cyan/12 hover:border-kova-cyan bg-kova-bg text-left transition-colors group" data-testid={`template-${tpl.provider}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-kova-card2 flex items-center justify-center text-kova-cyan" dangerouslySetInnerHTML={{ __html: tpl.icon_svg }} />
                      <div>
                        <h4 className="font-bold group-hover:text-kova-cyan transition-colors">{tpl.display_name}</h4>
                        <span className="text-xs text-kova-slate uppercase">{tpl.auth_method}</span>
                      </div>
                    </div>
                    <p className="text-kova-slate text-sm">{tpl.description}</p>
                  </button>
                ))}
              </div>
              <div className="border-t border-kova-cyan/12 pt-4">
                <button onClick={() => { setTemplateDialogOpen(false); resetForm(); setDialogOpen(true); }} className="w-full p-3 border border-dashed border-kova-cyan/12 hover:border-kova-cyan/25 text-kova-slate hover:text-white transition-colors text-sm" data-testid="connect-from-scratch">
                  <Plus size={16} className="inline mr-2" />Connect from scratch
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-3 p-4 bg-kova-bg border border-kova-cyan/12">
                <div className="w-12 h-12 bg-kova-card2 flex items-center justify-center text-kova-cyan" dangerouslySetInnerHTML={{ __html: selectedTemplate.icon_svg }} />
                <div>
                  <h3 className="font-bold text-lg">{selectedTemplate.display_name}</h3>
                  <p className="text-kova-slate text-sm">{selectedTemplate.description}</p>
                </div>
              </div>

              <div className="space-y-3">
                {selectedTemplate.credential_fields.map((field) => (
                  <div key={field.field_name} className="space-y-2">
                    <Label htmlFor={field.field_name}>{field.label}</Label>
                    <div className="relative">
                      <Input
                        id={field.field_name}
                        type={field.secret && !showSecrets[field.field_name] ? 'password' : 'text'}
                        value={templateCredentials[field.field_name] || ''}
                        onChange={(e) => setTemplateCredentials(prev => ({ ...prev, [field.field_name]: e.target.value }))}
                        placeholder={field.placeholder}
                        className="bg-kova-bg border-kova-cyan/12 focus:border-kova-cyan rounded-none font-mono text-sm pr-10"
                      />
                      {field.secret && (
                        <button type="button" onClick={() => setShowSecrets(prev => ({ ...prev, [field.field_name]: !prev[field.field_name] }))} className="absolute right-2 top-1/2 -translate-y-1/2 text-kova-slate hover:text-white">
                          {showSecrets[field.field_name] ? <EyeSlash size={18} /> : <Eye size={18} />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {testResult && (
                <div className={`p-3 border ${testResult.success ? 'border-kova-green/30 bg-kova-green/10' : 'border-kova-red/30 bg-kova-red/10'}`}>
                  <div className="flex items-center gap-2">
                    {testResult.success ? <CheckCircle size={18} className="text-kova-green" weight="fill" /> : <X size={18} className="text-kova-red" />}
                    <span className={testResult.success ? 'text-kova-green' : 'text-kova-red'}>{testResult.success ? 'Connection successful' : testResult.error}</span>
                    {testResult.response_time_ms && <span className="text-kova-slate text-xs ml-auto">{testResult.response_time_ms}ms</span>}
                  </div>
                  {testResult.info && (
                    <div className="mt-2 text-sm text-kova-silv">
                      {testResult.info.workspace && <span>Workspace: {testResult.info.workspace}</span>}
                      {testResult.info.username && <span>User: @{testResult.info.username}</span>}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t border-kova-cyan/12">
                <Button type="button" variant="outline" onClick={() => setSelectedTemplate(null)} className="border-kova-cyan/12 hover:bg-kova-card2 rounded-none">Back</Button>
                <Button type="button" onClick={handleTestTemplate} disabled={testingTemplate} className="flex-1 border-kova-cyan/12 bg-kova-card2 hover:bg-kova-cyan/12 rounded-none">
                  {testingTemplate ? 'Testing...' : 'Test Connection'}
                </Button>
                <Button type="button" onClick={handleCreateFromTemplate} disabled={!testResult?.success} className="flex-1 bg-kova-cyan text-kova-bg hover:bg-kova-cyan/90 rounded-none disabled:opacity-50">
                  Connect
                </Button>
              </div>

              {selectedTemplate.docs_url && (
                <a href={selectedTemplate.docs_url} target="_blank" rel="noopener noreferrer" className="block text-center text-kova-cyan text-sm hover:underline">
                  View {selectedTemplate.display_name} API Documentation →
                </a>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manual Connection Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="bg-kova-card border-kova-cyan/12 max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading font-bold text-xl">{editingConnection ? 'Edit Connection' : 'New Connection'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2"><Label htmlFor="name">Name</Label><Input id="name" data-testid="connection-name-input" value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} placeholder="My API" className="bg-kova-bg border-kova-cyan/12 focus:border-kova-cyan rounded-none" required /></div>
            <div className="space-y-2"><Label htmlFor="description">Description</Label><Input id="description" value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} placeholder="Brief description" className="bg-kova-bg border-kova-cyan/12 focus:border-kova-cyan rounded-none" /></div>
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-2"><Label>Method</Label><Select value={formData.method} onValueChange={(value) => setFormData(prev => ({ ...prev, method: value }))}><SelectTrigger className="bg-kova-bg border-kova-cyan/12 rounded-none"><SelectValue /></SelectTrigger><SelectContent className="bg-kova-card border-kova-cyan/12">{HTTP_METHODS.map((m) => <SelectItem key={m} value={m} className={getMethodColor(m)}>{m}</SelectItem>)}</SelectContent></Select></div>
              <div className="col-span-3 space-y-2"><Label htmlFor="base_url">Base URL</Label><Input id="base_url" value={formData.base_url} onChange={(e) => setFormData(prev => ({ ...prev, base_url: e.target.value }))} placeholder="https://api.example.com" className="bg-kova-bg border-kova-cyan/12 focus:border-kova-cyan rounded-none font-mono text-sm" required /></div>
            </div>
            <div className="space-y-2"><Label>Authentication</Label><Select value={formData.auth_type} onValueChange={(value) => setFormData(prev => ({ ...prev, auth_type: value, auth_config: {} }))}><SelectTrigger className="bg-kova-bg border-kova-cyan/12 rounded-none"><SelectValue /></SelectTrigger><SelectContent className="bg-kova-card border-kova-cyan/12">{AUTH_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select></div>
            {formData.auth_type === 'bearer' && <div className="space-y-2"><Label htmlFor="token">Bearer Token</Label><Input id="token" type="password" value={formData.auth_config.token || ''} onChange={(e) => setFormData(prev => ({ ...prev, auth_config: { ...prev.auth_config, token: e.target.value } }))} placeholder="Token" className="bg-kova-bg border-kova-cyan/12 focus:border-kova-cyan rounded-none font-mono text-sm" /></div>}
            {formData.auth_type === 'api_key' && (<><div className="space-y-2"><Label>Key Name</Label><Input value={formData.auth_config.key_name || ''} onChange={(e) => setFormData(prev => ({ ...prev, auth_config: { ...prev.auth_config, key_name: e.target.value } }))} placeholder="X-API-Key" className="bg-kova-bg border-kova-cyan/12 focus:border-kova-cyan rounded-none font-mono text-sm" /></div><div className="space-y-2"><Label>Key Value</Label><Input type="password" value={formData.auth_config.key_value || ''} onChange={(e) => setFormData(prev => ({ ...prev, auth_config: { ...prev.auth_config, key_value: e.target.value } }))} placeholder="Value" className="bg-kova-bg border-kova-cyan/12 focus:border-kova-cyan rounded-none font-mono text-sm" /></div></>)}
            <div className="space-y-2"><Label>Custom Headers</Label><div className="flex gap-2"><Input placeholder="Header" value={headerKey} onChange={(e) => setHeaderKey(e.target.value)} className="bg-kova-bg border-kova-cyan/12 focus:border-kova-cyan rounded-none font-mono text-sm" /><Input placeholder="Value" value={headerValue} onChange={(e) => setHeaderValue(e.target.value)} className="bg-kova-bg border-kova-cyan/12 focus:border-kova-cyan rounded-none font-mono text-sm" /><Button type="button" onClick={addHeader} variant="outline" className="border-kova-cyan/12 hover:bg-kova-card2 rounded-none"><Plus size={16} /></Button></div>{Object.entries(formData.headers).length > 0 && <div className="mt-2 space-y-1">{Object.entries(formData.headers).map(([k, v]) => <div key={k} className="flex items-center gap-2 py-1 px-2 bg-kova-bg border border-kova-cyan/12"><span className="font-mono text-xs text-kova-cyan">{k}:</span><span className="font-mono text-xs text-kova-silv flex-1 truncate">{v}</span><button type="button" onClick={() => removeHeader(k)} className="text-kova-slate hover:text-kova-red"><X size={14} /></button></div>)}</div>}</div>
            <div className="flex justify-end gap-3 pt-4 border-t border-kova-cyan/12"><Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-kova-cyan/12 hover:bg-kova-card2 rounded-none">Cancel</Button><Button type="submit" data-testid="save-connection-button" className="bg-kova-cyan text-kova-bg hover:bg-kova-cyan/90 rounded-none">{editingConnection ? 'Update' : 'Create'}</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      {/* API Catalog Dialog */}
      <Dialog open={catalogOpen} onOpenChange={setCatalogOpen}>
        <DialogContent className="bg-kova-card border-kova-cyan/12 max-w-4xl max-h-[90vh] overflow-hidden p-0">
          <DialogHeader className="sr-only"><DialogTitle>API Catalog</DialogTitle></DialogHeader>
          <div className="p-6">
            <APICatalog
              onSelectTemplate={handleCatalogSelect}
              onClose={() => setCatalogOpen(false)}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
